import type { FC } from 'react';
import React from 'react';
import { useQuery } from '@apollo/react-hooks';
import { FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { AccessDetailsGroupCountsQuery } from './AccessDetailsGroupCountsQuery.graphql';
import type {
	AccessDetailsGroupCountsQuery as AccessDetailsGroupCountsQueryType,
	AccessDetailsGroupCountsQueryVariables as AccessDetailsGroupCountsQueryVariablesType,
} from './__types__/AccessDetailsGroupCountsQuery';

type GroupCountComponentProps = {
	groupId?: string | null;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupCountLine = styled.div({
	font: token('font.body.small'),
	color: token('color.text.subtlest'),
});

const useGroupCount = (id: GroupCountComponentProps['groupId']) => {
	const { loading, data } = useQuery<
		AccessDetailsGroupCountsQueryType,
		AccessDetailsGroupCountsQueryVariablesType
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		AccessDetailsGroupCountsQuery,
		{
			variables: { groupIdList: id !== undefined ? [id] : null },
			skip: !id,
		},
	);

	if (loading) return null;

	const count = data?.groupCounts?.groupCounts?.[0]?.value;

	if (typeof count === 'number') {
		return count;
	}
};

export const GroupCountComponent: FC<GroupCountComponentProps> = ({ groupId }) => {
	const count = useGroupCount(groupId);

	if (!count) {
		return null;
	}

	return (
		<GroupCountLine>
			<FormattedMessage
				id="space-access-details.access-details-table.header.principal"
				defaultMessage="{count, plural, one {# person} other {# people}}"
				description="Count of people in a given group"
				values={{
					count,
				}}
			/>
		</GroupCountLine>
	);
};
